import { useCallback, useMemo, useState } from "react";
import {
    applyAccountSelection,
    applyLineItemFieldChange,
    mapBackendLineItemValidationErrorsToFields,
    mergeLineEditFieldErrors,
    validateLineEditForm,
} from "./lineItemEditValidation";
import { resolveLineItemFields } from "./lineItemFields";
import type { TReviewLineItem } from "./types";
import type { TPropertyCodeKind } from "@/modules/rev_apac/review/masterPropertyCodeSearch";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";

type TUseLineItemEditorDeps = {
    /** File type of the file under review — decides which fields are mandatory. */
    fileType: string | undefined;
    /** Server-side master property-code search, scoped to one kind of code. */
    searchMasterPropertyCodes: (
        query: string,
        kind: TPropertyCodeKind,
    ) => Promise<TSearchableOption[]>;
    /** Account-code field search: options display the code as label. */
    searchYardiAccountsByCode: (query: string) => Promise<TSearchableOption[]>;
    /** Account-name field search: options display the name as label. */
    searchYardiAccountsByName: (query: string) => Promise<TSearchableOption[]>;
    onEditLineItem: (lineItem: TReviewLineItem, updates: Partial<TReviewLineItem>) => Promise<void>;
};

/**
 * Encapsulates the line-item edit modal: form state, field validation, and save.
 * Extracted from ViewFileReview to keep the view focused on layout.
 */
export function useLineItemEditor({
    fileType,
    searchMasterPropertyCodes,
    searchYardiAccountsByCode,
    searchYardiAccountsByName,
    onEditLineItem,
}: TUseLineItemEditorDeps) {
    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingLineItem, setEditingLineItem] = useState<TReviewLineItem | null>(null);
    const [editFormData, setEditFormData] = useState<Partial<TReviewLineItem>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    /** Inline field errors only after user edits or tries to save (avoids noisy red state on open). */
    const [lineEditShowFieldErrors, setLineEditShowFieldErrors] = useState(false);

    /** Field definitions for this file type — FinJournals drop the required flags. */
    const editFields = useMemo(() => resolveLineItemFields(fileType), [fileType]);

    const mergedEditFieldErrors = useMemo(
        () =>
            mergeLineEditFieldErrors({
                server: mapBackendLineItemValidationErrorsToFields(
                    editingLineItem?.validationErrors ?? [],
                ),
                client: validateLineEditForm({ form: editFormData, fields: editFields }),
                showClientErrors: lineEditShowFieldErrors,
                editingLineItem,
                editFormData,
            }),
        [editingLineItem, editFormData, lineEditShowFieldErrors, editFields],
    );

    // Handle edit click - open modal with line item data
    const handleEditClick = useCallback((lineItem: TReviewLineItem) => {
        setLineEditShowFieldErrors(false);
        setEditingLineItem(lineItem);
        setEditFormData({ ...lineItem });
        setEditError(null);
        setEditModalOpen(true);
    }, []);

    // Handle form field change for non-account fields
    const handleFieldChange = useCallback((key: keyof TReviewLineItem, value: string | number) => {
        setLineEditShowFieldErrors(true);
        setEditFormData((prev) => applyLineItemFieldChange(prev, key, value));
        setEditError(null);
    }, []);

    // Handle account selection from either the code or name SearchableSelect
    const handleAccountSelect = useCallback((option: TSearchableOption | null) => {
        setLineEditShowFieldErrors(true);
        setEditFormData((prev) => applyAccountSelection(prev, option));
        setEditError(null);
    }, []);

    // Each mapped-value dropdown searches only its own kind of master code, so an
    // entity can never be picked as an asset.
    const searchEntityCodes = useCallback(
        (query: string) => searchMasterPropertyCodes(query, "entity"),
        [searchMasterPropertyCodes],
    );
    const searchAssetCodes = useCallback(
        (query: string) => searchMasterPropertyCodes(query, "asset"),
        [searchMasterPropertyCodes],
    );
    const searchInvestmentCodes = useCallback(
        (query: string) => searchMasterPropertyCodes(query, "investment"),
        [searchMasterPropertyCodes],
    );

    // Handle propcode selection from the master-data SearchableSelect.
    const handlePropcodeSelect = useCallback((option: TSearchableOption | null) => {
        setLineEditShowFieldErrors(true);
        setEditFormData((prev) => ({ ...prev, propcode: option ? option.value : null }));
        setEditError(null);
    }, []);

    // Asset and investment follow the same shape; clearing sets null (the save sends
    // null to unmap the line, never an empty string).
    const handleAssetSelect = useCallback((option: TSearchableOption | null) => {
        setLineEditShowFieldErrors(true);
        setEditFormData((prev) => ({ ...prev, mappedAsset: option ? option.value : null }));
        setEditError(null);
    }, []);

    const handleInvestmentSelect = useCallback((option: TSearchableOption | null) => {
        setLineEditShowFieldErrors(true);
        setEditFormData((prev) => ({ ...prev, mappedInvestment: option ? option.value : null }));
        setEditError(null);
    }, []);

    // Handle save with required field validation
    const handleSaveLineItem = useCallback(async () => {
        if (!editingLineItem) return;

        const snapshotErrors = mergeLineEditFieldErrors({
            server: mapBackendLineItemValidationErrorsToFields(
                editingLineItem.validationErrors ?? [],
            ),
            client: validateLineEditForm({ form: editFormData, fields: editFields }),
            showClientErrors: true,
            editingLineItem,
            editFormData,
        });
        if (Object.keys(snapshotErrors).length > 0) {
            setLineEditShowFieldErrors(true);
            const firstInvalid = editFields.find((f) => snapshotErrors[f.key]);
            if (firstInvalid) {
                window.requestAnimationFrame(() => {
                    document.getElementById(`edit-${String(firstInvalid.key)}`)?.focus();
                });
            }
            return;
        }

        // Capture before clearing so the async call still has the data
        const lineItemToSave = editingLineItem;
        const formDataToSave = editFormData;

        // Close immediately so the background reload doesn't show the modal again
        setEditModalOpen(false);
        setEditingLineItem(null);
        setEditFormData({});
        setIsSaving(true);
        setEditError(null);
        try {
            await onEditLineItem(lineItemToSave, formDataToSave);
            // onEditLineItem already revalidates and reloads file + line items
        } catch (error) {
            console.error("Failed to save line item:", error);
            // Re-open with the original data so the user can correct the error
            setEditingLineItem(lineItemToSave);
            setEditFormData(formDataToSave);
            setEditError(error instanceof Error ? error.message : "Failed to save line item");
            setEditModalOpen(true);
        } finally {
            setIsSaving(false);
        }
    }, [editingLineItem, editFormData, editFields, onEditLineItem]);

    // Handle modal close
    const handleCloseEditModal = useCallback((open: boolean) => {
        if (!open) {
            setEditModalOpen(false);
            setEditingLineItem(null);
            setEditFormData({});
            setEditError(null);
            setLineEditShowFieldErrors(false);
        }
    }, []);

    return {
        editModalOpen,
        editingLineItem,
        editFormData,
        isSaving,
        editError,
        editFields,
        mergedEditFieldErrors,
        handleEditClick,
        handleFieldChange,
        handleAccountSelect,
        handlePropcodeSelect,
        handleAssetSelect,
        handleInvestmentSelect,
        searchEntityCodes,
        searchAssetCodes,
        searchInvestmentCodes,
        searchYardiAccountsByCode,
        searchYardiAccountsByName,
        handleSaveLineItem,
        handleCloseEditModal,
    };
}
